import Accordion from 'components/Accordion'
import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import LOCALES from 'constants/locales'
import useLocale from 'hooks/useLocale'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import Ul from 'ui/Ul'
import getLocalesDropdownOptions from 'utils/getLocalesDropdownOptions'
import globeIcon from './icons/icon-globe.svg'
import mobileCheckIcon from './icons/mobile_check_icon.svg'

const MobileLocaleAccordion: React.FC<ILocalesDropdown> = ({ pageLocales, type }) => {
  const { locale, pathname } = useLocale()

  const localesOptions = useMemo(
    () => getLocalesDropdownOptions(pathname, locale, LOCALES, pageLocales),
    [pathname, locale]
  )
  const currentlocaleName = useMemo(() => LOCALES.find(item => locale == item.code), [locale])

  return (
    <Accordion
      renderButton={(onToggle, isActive) => (
        <AccordionButton $type={type} $isActive={isActive} onClick={onToggle}>
          <NameContainer $type={type}>
            <FlagImg src={globeIcon} />
            <span>{currentlocaleName?.name}</span>
          </NameContainer>
        </AccordionButton>
      )}
      renderContent={() => {
        return (
          <Ul>
            <DropdownItemContainer $type={type}>
              {localesOptions.map(({ name, url }, index) => {
                return (
                  <DropdownItem
                    $type={type}
                    activeClassName='active-locale'
                    key={index}
                    to={url}
                    withLocale={false}
                    replace>
                    {name}
                  </DropdownItem>
                )
              })}
            </DropdownItemContainer>
          </Ul>
        )
      }}
    />
  )
}

export default MobileLocaleAccordion

interface ILocalesDropdown {
  type: THeaderType
  pageLocales?: string[]
}

const DropdownItemContainer = styled.div<{ $type: THeaderType }>`
  & > .active-locale {
    color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

    &::before {
      content: '';
      position: absolute;
      top: 35%;
      right: 35px;
      height: 18px;
      width: 19px;
      background-image: url(${mobileCheckIcon});
      background-repeat: no-repeat;
      background-position: center;
    }
  }
`

const DropdownItem = styled(LinkWithLocale)<{ $type: THeaderType }>`
  display: block;
  position: relative;
  text-align: start;

  padding: 17px 40px;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 0.875rem;

  color: ${({ $type, theme: { colors } }) => ($type === 'primary' ? colors.newDarkColor.softNavy : colors.lightBlue)};
  text-align: inherit;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;
  border-top: 1px solid transparent;

  &:hover {
    color: ${({ $type, theme: { colors } }) =>
      $type === 'primary' ? colors.newDarkColor.vividBlue : colors.newLightColor.white};
    border-top-color: rgba(48, 113, 238, 0.2);
    background-color: ${({ $type, theme: { colors } }) =>
      $type === 'primary' ? colors.newDarkColor.white : 'rgba(48, 113, 238, 0.2)'};
  }
`

const FlagImg = styled.img`
  margin-inline-end: 16px;
`

const NameContainer = styled.div<{ $type: THeaderType }>`
  display: flex;
  align-items: center;

  & > span {
    color: ${({ $type, theme: { colors } }) =>
      $type === 'primary' ? colors.newDarkColor.softNavy : colors.newLightColor.white};
    font-size: 14px;
  }
`

const AccordionButton = styled.div<{ $type: THeaderType; $isActive: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.white : colors.lightBlue)};
  font-size: 14px;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  -webkit-tap-highlight-color: transparent;
  padding: 16px 40px;
  border-bottom: 1px solid rgba(48, 113, 238, 0.2);
  cursor: pointer;
  &::after {
    display: inline-block;
    vertical-align: 3.96px;
    content: '';
    width: 9px;
    height: 6px;
    margin-inline-start: auto;
    border-style: solid;
    border-width: 6px 5px 0 5px;
    border-color: #6b7b9f transparent transparent transparent;
    transition: all 0.1s ease-out;
  }
  ${({ $isActive, $type, theme: { colors } }) =>
    $isActive &&
    `
    ::after {
      transition: all 0.1s ease-out;
      border-color: ${
        $type === 'primary' ? '#97b8f7' : colors.newDarkColor.cyanBlue
      }  transparent transparent transparent;
      transform: rotate(180deg);
    }
  `}
`
